/**
 * Sanitization Layer - Phase 14
 * 
 * Removes sensitive data from diagnostics bundles before export.
 * Ensures no secrets, tokens, or excessive PII are exposed.
 */

// ==================== SENSITIVE FIELD PATTERNS ====================

const SENSITIVE_KEYS = [
  'access_token', 'accessToken', 'auth_token', 'authToken',
  'refresh_token', 'refreshToken', 'bearer',
  'api_key', 'apiKey', 'api_secret', 'apiSecret',
  'secret', 'password', 'passwd', 'pwd',
  'private_key', 'privateKey', 'secret_key', 'secretKey',
  'spaces_key', 'spacesKey', 'aws_secret', 'awsSecret',
  'stripe_key', 'stripeKey', 'processor_key', 'processorKey',
  'card_number', 'cardNumber', 'pan', 'cvv', 'cvc',
  'ssn', 'social_security', 'socialSecurity',
  'authorization', 'x-auth-token', 'x-api-key',
  'cookie', 'session_id', 'sessionId'
];

const SENSITIVE_VALUE_PATTERNS = [
  /^Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/i, // JWT
  /^sk_[a-zA-Z0-9]+$/i, // Stripe secret key
  /^pk_[a-zA-Z0-9]+$/i, // Stripe public key (less sensitive but still redact)
  /^\d{13,19}$/, // Card number pattern
  /^[A-Za-z0-9+/=]{20,}$/, // Base64 encoded secrets
  /eyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/i // JWT in string
];

const SIGNED_URL_PARAMS = [
  'X-Amz-Signature', 'X-Amz-Credential', 'X-Amz-Security-Token',
  'Signature', 'sig', 'token', 'access_token'
];

// ==================== SANITIZATION FUNCTIONS ====================

/**
 * Sanitize complete diagnostics bundle
 * @param {Object} bundle - Raw diagnostics bundle
 * @returns {Object} - Sanitized bundle
 */
export function sanitizeDiagnosticsBundle(bundle) {
  if (!bundle) return bundle;
  
  const sanitized = deepClone(bundle);
  
  // Sanitize each section
  if (sanitized.appContext) {
    sanitized.appContext = sanitizeConfigSummary(sanitized.appContext);
  }
  
  if (sanitized.terminalContext) {
    sanitized.terminalContext = sanitizeConfigSummary(sanitized.terminalContext);
  }
  
  if (sanitized.replaySummary?.recentEntries) {
    sanitized.replaySummary.recentEntries = sanitized.replaySummary.recentEntries.map(
      entry => sanitizeSyncLogEntry(entry)
    );
  }
  
  if (sanitized.incidentSummary?.unresolvedIncidents) {
    sanitized.incidentSummary.unresolvedIncidents = sanitized.incidentSummary.unresolvedIncidents.map(
      incident => sanitizeIncidentForExport(incident)
    );
  }
  
  if (sanitized.networkDiagnostics) {
    sanitized.networkDiagnostics = sanitizeConfigSummary(sanitized.networkDiagnostics);
  }
  
  if (sanitized.featureFlags) {
    sanitized.featureFlags = sanitizeConfigSummary(sanitized.featureFlags);
  }
  
  if (sanitized.metadata) {
    sanitized.metadata = stripSensitiveFields(sanitized.metadata);
  }
  
  // Mark as sanitized
  sanitized.sanitization = {
    secretsRemoved: true,
    piiLevel: 'minimal',
    signedUrlsRemoved: true,
    sanitizedAt: Date.now()
  };
  
  return sanitized;
}

/**
 * Sanitize incident for export
 * @param {Object} incident - Raw incident object
 * @returns {Object} - Sanitized incident
 */
export function sanitizeIncidentForExport(incident) {
  if (!incident) return incident;
  
  const sanitized = deepClone(incident);
  
  // Remove potentially sensitive fields from actor
  if (sanitized.actor) {
    sanitized.actor = {
      userId: sanitized.actor.userId || null,
      role: sanitized.actor.role || null,
      name: redactName(sanitized.actor.name)
    };
  }
  
  // Sanitize metadata
  if (sanitized.metadata) {
    sanitized.metadata = stripSensitiveFields(sanitized.metadata);
  }
  
  // Remove full payload if present
  if (sanitized.payload) {
    sanitized.payload = '[REDACTED]';
  }
  
  // Sanitize any URLs
  if (sanitized.endpoint) {
    sanitized.endpoint = sanitizeUrl(sanitized.endpoint);
  }
  
  return sanitized;
}

/**
 * Sanitize sync log entry
 * @param {Object} entry - Raw sync log entry
 * @returns {Object} - Sanitized entry
 */
export function sanitizeSyncLogEntry(entry) {
  if (!entry) return entry;
  
  const sanitized = deepClone(entry);
  
  // Remove full payload
  if (sanitized.payload) {
    sanitized.payload = '[REDACTED]';
  }
  
  // Sanitize error messages (may contain sensitive data)
  if (sanitized.message) {
    sanitized.message = sanitizeErrorMessage(sanitized.message);
  }
  
  // Strip any embedded tokens
  sanitized = stripSensitiveFields(sanitized);
  
  return sanitized;
}

/**
 * Sanitize queue item
 * @param {Object} item - Raw queue item
 * @returns {Object} - Sanitized item
 */
export function sanitizeQueueItem(item) {
  if (!item) return item;
  
  const sanitized = deepClone(item);
  
  // Remove payload contents (may contain sensitive data)
  if (sanitized.payload) {
    sanitized.payload = {
      _sanitized: true,
      _fieldCount: Object.keys(item.payload || {}).length
    };
  }
  
  // Remove headers (may contain auth tokens)
  if (sanitized.headers) {
    sanitized.headers = {
      _sanitized: true,
      _hadAuth: !!(item.headers?.Authorization || item.headers?.authorization)
    };
  }
  
  // Keep operational fields
  return {
    id: sanitized.id,
    type: sanitized.type,
    status: sanitized.status,
    createdAt: sanitized.createdAt,
    updatedAt: sanitized.updatedAt,
    retryCount: sanitized.retryCount,
    lastError: sanitizeErrorMessage(sanitized.lastError),
    entityType: sanitized.entityType,
    merchantId: sanitized.merchantId,
    priority: sanitized.priority,
    payload: sanitized.payload,
    headers: sanitized.headers
  };
}

/**
 * Sanitize config/summary objects
 * @param {Object} config - Configuration object
 * @returns {Object} - Sanitized config
 */
export function sanitizeConfigSummary(config) {
  if (!config) return config;
  return stripSensitiveFields(deepClone(config));
}

/**
 * Strip sensitive fields from object recursively
 * @param {Object} obj - Object to sanitize
 * @returns {Object} - Sanitized object
 */
export function stripSensitiveFields(obj) {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') {
    return sanitizeValue(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => stripSensitiveFields(item));
  }
  
  const result = {};
  
  for (const [key, value] of Object.entries(obj)) {
    // Check if key is sensitive
    if (isSensitiveKey(key)) {
      result[key] = '[REDACTED]';
      continue;
    }
    
    // Recursively sanitize nested objects
    if (typeof value === 'object' && value !== null) {
      result[key] = stripSensitiveFields(value);
    } else {
      result[key] = sanitizeValue(value);
    }
  }
  
  return result;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Check if a key name indicates sensitive data
 * @param {string} key
 * @returns {boolean}
 */
function isSensitiveKey(key) {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sk => lowerKey.includes(sk.toLowerCase()));
}

/**
 * Sanitize a single value
 * @param {any} value
 * @returns {any}
 */
function sanitizeValue(value) {
  if (typeof value !== 'string') return value;
  
  // Check for sensitive patterns
  for (const pattern of SENSITIVE_VALUE_PATTERNS) {
    if (pattern.test(value)) {
      return '[REDACTED]';
    }
  }
  
  // Check for URLs with signed parameters
  if (value.includes('?') && value.includes('http')) {
    return sanitizeUrl(value);
  }
  
  return value;
}

/**
 * Sanitize URL by removing sensitive query parameters
 * @param {string} url
 * @returns {string}
 */
function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return url;
  
  try {
    const urlObj = new URL(url, 'http://dummy');
    
    // Remove sensitive query params
    for (const param of SIGNED_URL_PARAMS) {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    }
    
    // Return path + sanitized query string
    const path = urlObj.pathname + urlObj.search;
    return url.startsWith('http') ? urlObj.toString() : path;
  } catch {
    // If URL parsing fails, return path portion only
    const qIndex = url.indexOf('?');
    return qIndex > 0 ? url.substring(0, qIndex) + '?[PARAMS_REDACTED]' : url;
  }
}

/**
 * Sanitize error message
 * @param {string} message
 * @returns {string}
 */
function sanitizeErrorMessage(message) {
  if (!message || typeof message !== 'string') return message;
  
  // Remove potential tokens from error messages
  let sanitized = message;
  
  // Remove JWTs
  sanitized = sanitized.replace(
    /eyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi,
    '[JWT_REDACTED]'
  );
  
  // Remove API keys patterns
  sanitized = sanitized.replace(
    /[a-zA-Z0-9_-]{32,}/g,
    (match) => match.length > 40 ? '[KEY_REDACTED]' : match
  );
  
  return sanitized;
}

/**
 * Redact name while keeping first initial
 * @param {string} name
 * @returns {string}
 */
function redactName(name) {
  if (!name || typeof name !== 'string') return name;
  const parts = name.split(' ');
  if (parts.length === 1) {
    return name.charAt(0) + '***';
  }
  return parts[0].charAt(0) + '*** ' + parts[parts.length - 1].charAt(0) + '***';
}

/**
 * Deep clone an object
 * @param {Object} obj
 * @returns {Object}
 */
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj);
  if (Array.isArray(obj)) return obj.map(item => deepClone(item));
  
  const cloned = {};
  for (const key of Object.keys(obj)) {
    cloned[key] = deepClone(obj[key]);
  }
  return cloned;
}

export default {
  sanitizeDiagnosticsBundle,
  sanitizeIncidentForExport,
  sanitizeSyncLogEntry,
  sanitizeQueueItem,
  sanitizeConfigSummary,
  stripSensitiveFields
};
